import {
  AFFIXES,
  affixTotals,
  BASE_COMBAT_STATS,
  bossDps,
  type CombatStats,
  combatStatsOf,
  type Equipped,
  getItem,
  wornPieces,
} from '@shefira/shared';

import { formatDps } from './item-tooltip.js';
import { uiDecimal, uiPercent } from './text.js';
import type { KitLine } from './kit.js';

/**
 * What the affixes worn add to a fight (ADR 0057), as lines of the
 * character sheet: the crit always (it has a base), the rest once something
 * raises it. Display only: the server computes the same with the same rules.
 */

/** The fight's figures of what is worn. */
export function combatOfEquipped(equipped: Equipped): CombatStats {
  return combatStatsOf(wornPieces(equipped, getItem).map((piece) => piece.affixes));
}

function percent(share: number): string {
  return uiPercent(Math.round(share * 100));
}

/** Said on the line when the sum worn goes past what counts. */
function capNote(equipped: Equipped, id: keyof typeof AFFIXES): string | undefined {
  const cap = AFFIXES[id].cap;
  const total = affixTotals(wornPieces(equipped, getItem).map((piece) => piece.affixes))[id];
  return cap !== undefined && total > cap ? 'plafond atteint' : undefined;
}

export function combatLines(equipped: Equipped, dps: number): KitLine[] {
  const combat = combatOfEquipped(equipped);
  const lines: KitLine[] = [
    {
      icon: 'stat-best-hit',
      label: 'Critique',
      value: percent(combat.critChance),
      note: `×${uiDecimal(combat.critMultiplier, 2).replace(/0$/, '')}`,
      quietNote: true,
      title: capNote(equipped, 'crit-chance') ?? 'Chance de coup critique, et ce qu’il multiplie',
    },
  ];
  if (combat.bossDamage > 0) {
    lines.push({
      icon: 'stat-bosses',
      label: 'DPS boss et élites',
      value: formatDps(bossDps(dps, combat)),
      note: `+${percent(combat.bossDamage)}`,
      title: capNote(equipped, 'boss-damage') ?? 'DPS moyen contre un boss ou une élite',
    });
  }
  if (combat.cooldownReduction > 0) {
    lines.push({
      icon: 'stat-spells',
      label: 'Recharge',
      value: `-${percent(combat.cooldownReduction)}`,
      title: capNote(equipped, 'cooldown') ?? 'Recharge des sorts raccourcie',
    });
  }
  if (combat.energyPerSecond > BASE_COMBAT_STATS.energyPerSecond) {
    lines.push({
      icon: 'stat-spells',
      label: 'Énergie par seconde',
      value: formatDps(combat.energyPerSecond),
      title: capNote(equipped, 'energy') ?? 'Énergie regagnée chaque seconde',
    });
  }
  if (combat.lifesteal > 0) {
    lines.push({
      icon: 'stat-health',
      label: 'Vol de vie',
      value: percent(combat.lifesteal),
      title: capNote(equipped, 'lifesteal') ?? 'Part des dégâts infligés rendue en vie',
    });
  }
  return lines;
}
